'use client';

import Link from 'next/link';
import PropTypes from 'prop-types';
import { Box, Container, Grid, Typography } from '@mui/material';
import { NEWS_PATH } from '@/consts/routes';
import WebsiteInteriorScrollLayout from './WebsiteInteriorScrollLayout';
import NewsMediaPageHeader from './NewsMediaPageHeader';

const NEWS_INTRO_COPY =
  'Stay in the loop with all the latest updates from NIICO and important industry news. Our team is constantly on point with the latest and greatest trends, ensuring your space is a timeless treasure.';

function NewsArticleCard({ article, basePath }) {
  return (
    <Box
      component={Link}
      href={`${basePath}/${article.slug}`}
      sx={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        '&:hover .news-card-image': {
          opacity: 0.88,
        },
      }}
    >
      <Box
        className="news-card-image"
        component="img"
        src={article.coverSrc}
        alt={article.title}
        sx={{
          width: '100%',
          height: { xs: 240, sm: 280, md: 300 },
          objectFit: 'cover',
          display: 'block',
          transition: 'opacity 0.3s ease',
        }}
      />
      <Box sx={{ pt: 1.5 }}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          {article.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {article.source}
        </Typography>
      </Box>
    </Box>
  );
}

NewsArticleCard.propTypes = {
  article: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    source: PropTypes.string.isRequired,
    coverSrc: PropTypes.string.isRequired,
  }).isRequired,
  basePath: PropTypes.string.isRequired,
};

export default function NewsPage({
  articles,
  basePath = NEWS_PATH,
  headerTitle = 'In The News',
  introCopy = NEWS_INTRO_COPY,
  emptyMessage = 'No news articles available yet.',
}) {
  return (
    <WebsiteInteriorScrollLayout>
      <NewsMediaPageHeader title={headerTitle} introCopy={introCopy} />
      <Box component="section" sx={{ pb: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          {articles.length === 0 ? (
            <Typography color="text.secondary">{emptyMessage}</Typography>
          ) : (
            <Grid container spacing={{ xs: 3, md: 4 }}>
              {articles.map(article => (
                <Grid key={article.slug} item xs={12} sm={6} md={4}>
                  <NewsArticleCard article={article} basePath={basePath} />
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>
    </WebsiteInteriorScrollLayout>
  );
}

NewsPage.propTypes = {
  articles: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      source: PropTypes.string.isRequired,
      coverSrc: PropTypes.string.isRequired,
    }),
  ).isRequired,
  basePath: PropTypes.string,
  headerTitle: PropTypes.string,
  introCopy: PropTypes.string,
  emptyMessage: PropTypes.string,
};
